import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminDelivery() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => { api.get("/admin/delivery").then((r) => setCfg(r.data)); }, []);

  const save = async () => {
    const { id, ...payload } = cfg;
    await api.put("/admin/delivery", payload);
    toast.success("Configuration sauvegardée");
  };

  if (!cfg) return null;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-delivery">
      <div>
        <h1 className="font-serif text-4xl text-[var(--dsf-green)]">Configuration livraison</h1>
        <p className="text-[var(--dsf-text-muted)] text-sm mt-1">Pilotez vos transporteurs partenaires et tarifs</p>
      </div>

      <div className="bg-white border border-[var(--dsf-border)] p-6 space-y-5">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-burgundy)]">Modes de livraison disponibles</div>
        {[
          { key: "delivery_enabled", label: "Livraison à domicile", desc: "Livraison standard à l'adresse client" },
          { key: "click_collect_enabled", label: "Ramassage en boutique", desc: "Retrait gratuit sur place" },
        ].map((t) => (
          <div key={t.key} className="flex items-center justify-between border-b border-[var(--dsf-border)] pb-4 last:border-b-0" data-testid={`delivery-toggle-${t.key}`}>
            <div>
              <div className="font-medium">{t.label}</div>
              <div className="text-xs text-[var(--dsf-text-muted)]">{t.desc}</div>
            </div>
            <Switch checked={cfg[t.key] ?? true} onCheckedChange={(v) => setCfg({ ...cfg, [t.key]: v })} />
          </div>
        ))}
      </div>

      <div className="bg-white border border-[var(--dsf-border)] p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-burgundy)]">Frais de livraison standard (USD)</Label>
          <Input type="number" step="0.01" value={cfg.delivery_fee} onChange={(e) => setCfg({ ...cfg, delivery_fee: parseFloat(e.target.value) })} className="mt-1" data-testid="delivery-fee-input" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-burgundy)]">Seuil livraison gratuite (USD)</Label>
          <Input type="number" step="0.01" value={cfg.free_delivery_threshold} onChange={(e) => setCfg({ ...cfg, free_delivery_threshold: parseFloat(e.target.value) })} className="mt-1" data-testid="free-threshold-input" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-burgundy)]">Délai min (min)</Label>
          <Input type="number" value={cfg.estimated_time_min} onChange={(e) => setCfg({ ...cfg, estimated_time_min: parseInt(e.target.value) })} className="mt-1" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-burgundy)]">Délai max (min)</Label>
          <Input type="number" value={cfg.estimated_time_max} onChange={(e) => setCfg({ ...cfg, estimated_time_max: parseInt(e.target.value) })} className="mt-1" />
        </div>
      </div>

      <div className="bg-white border border-[var(--dsf-border)] p-6 space-y-4">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-burgundy)] font-bold">Lieu 1 · Restaurant</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Nom affiché</Label><Input value={cfg.restaurant_name || ""} onChange={(e) => setCfg({ ...cfg, restaurant_name: e.target.value })} className="mt-1" data-testid="restaurant-name-input" /></div>
          <div><Label>Téléphone</Label><Input value={cfg.restaurant_phone || ""} onChange={(e) => setCfg({ ...cfg, restaurant_phone: e.target.value })} className="mt-1" /></div>
          <div className="md:col-span-2"><Label>Adresse complète</Label><Input value={cfg.restaurant_address || ""} onChange={(e) => setCfg({ ...cfg, restaurant_address: e.target.value })} className="mt-1" data-testid="restaurant-address-input" /></div>
          <div className="md:col-span-2"><Label>Horaires d'ouverture</Label><Input value={cfg.restaurant_hours || ""} onChange={(e) => setCfg({ ...cfg, restaurant_hours: e.target.value })} className="mt-1" placeholder="ex: Lun–Dim · 11h–22h" /></div>
        </div>
      </div>

      <div className="bg-white border border-[var(--dsf-border)] p-6 space-y-4">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-burgundy)] font-bold">Lieu 2 · Food Truck</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Nom affiché</Label><Input value={cfg.foodtruck_name || ""} onChange={(e) => setCfg({ ...cfg, foodtruck_name: e.target.value })} className="mt-1" data-testid="foodtruck-name-input" /></div>
          <div><Label>Téléphone</Label><Input value={cfg.foodtruck_phone || ""} onChange={(e) => setCfg({ ...cfg, foodtruck_phone: e.target.value })} className="mt-1" /></div>
          <div className="md:col-span-2"><Label>Adresse / Localisation actuelle</Label><Input value={cfg.foodtruck_address || ""} onChange={(e) => setCfg({ ...cfg, foodtruck_address: e.target.value })} className="mt-1" data-testid="foodtruck-address-input" placeholder="ex: Houston · varies daily — Instagram" /></div>
          <div className="md:col-span-2"><Label>Horaires</Label><Input value={cfg.foodtruck_hours || ""} onChange={(e) => setCfg({ ...cfg, foodtruck_hours: e.target.value })} className="mt-1" placeholder="ex: Service midi · suivi @ig" /></div>
        </div>
      </div>

      <button onClick={save} className="btn-primary" data-testid="delivery-save-button">Enregistrer la configuration</button>
    </div>
  );
}
